import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { openVrApp, vrBridgeError } from '../../auth/vrBridge'
import { ClubMark } from '../../components/ClubMark'
import { Collapsible } from '../../components/Collapsible'
import {
  know,
  type PerformanceDto,
  type ProfileDto,
  type RankingDto,
  type ResponseSummary,
  PRE_SESSION_CODE,
} from '../../api/know'
import { getMyPhysiologySessions, type PhysiologySessionEntry } from '../../api/physiology'
import { LiveSignalsCard, SignalsSummary } from './SignalsPanels'
import { RankingSection } from './RankingCard'
import { SessionsSection } from './SessionsSection'
import { ReportPreview } from './ReportPreview'
import { buildSignalsModel } from './signalsModel'
import { mergeSessions } from './sessionsModel'
import {
  POSITION_LABELS,
  VR_EXPERIENCE_LABELS,
  footLabel,
  labelOf,
} from './profileLabels'

/**
 * `/data` — el área personal del jugador, UNA página, jerarquizada de arriba
 * hacia abajo por lo que el jugador mira primero (rediseño de mg,
 * 2026-09-07 tarde; la versión de la mañana tenía todo plano y DOS tablas de
 * sesiones):
 *
 *   1. **Cabecera** — quién sos, tu puesto en el ranking y el próximo paso
 *      (cuestionario pendiente → link; si no, Jugar). Un solo bloque.
 *   2. **En vivo** — sólo si tenés el kit puesto ahora mismo.
 *   3. **Ranking** — el podio completo.
 *   4. **Performance** — cinco cifras. El desglose de resultados y los KPIs
 *      de señales viven en un colapsable.
 *   5. **Sesiones** — UNA lista (`SessionsSection`): cada fila se abre con su
 *      resultado, su chequeo previo y sus ciclos de regulación.
 *   6. **Cuestionarios** y **Perfil** — colapsables, al final.
 *
 * Los fetches (performance, respuestas, perfil, señales) salen en paralelo y
 * cada sección tolera el fallo de las otras. Las rutas viejas
 * `/data/performance` y `/data/physiology` redirigen acá.
 */
export function KnowHome() {
  const { user } = useAuth()
  const [perf, setPerf] = useState<PerformanceDto | null>(null)
  const [responses, setResponses] = useState<ResponseSummary[] | null>(null)
  const [profile, setProfile] = useState<ProfileDto | null>(null)
  const [physio, setPhysio] = useState<PhysiologySessionEntry[] | null>(null)
  const [ranking, setRanking] = useState<RankingDto | null>(null)

  useEffect(() => {
    know.performance().then(setPerf).catch(() => setPerf(null))
    know.responses().then((r) => setResponses(r.items)).catch(() => setResponses([]))
    know.profile().then(setProfile).catch(() => setProfile(null))
    getMyPhysiologySessions()
      .then((d) => setPhysio(d.sessions))
      .catch(() => setPhysio([]))
  }, [])

  const signals = useMemo(
    () => (physio ? buildSignalsModel(physio) : null),
    [physio],
  )

  // La lista aparece recién cuando llegaron las tres fuentes: media lista que
  // después crece y reordena filas es peor que un "Cargando…" de un segundo.
  const sessions = useMemo(() => {
    if (!signals || !perf || !responses) return null
    return mergeSessions(signals.sessions, perf.recent_sessions, responses)
  }, [signals, perf, responses])

  const onRanking = useCallback((dto: RankingDto) => setRanking(dto), [])

  return (
    <div className="flex flex-col gap-lg">
      <Hero
        profile={profile}
        ranking={ranking}
        pending={user?.pending_questionnaires?.[0] ?? null}
      />
      <ReportPreview />
      <details className="card"><summary className="cursor-pointer font-medium">Señales en vivo</summary><div className="mt-md"><LiveSignalsCard /></div></details>
      <RankingSection userId={user?.user_id} onLoaded={onRanking} />
      <PerformanceSection perf={perf} signals={signals} />
      <SessionsSection sessions={sessions} />
      <QuestionnairesBlock responses={responses} />
      <ProfileBlock profile={profile} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// 1. Cabecera: identidad + puesto + próximo paso
// ---------------------------------------------------------------------------

function Hero({
  profile,
  ranking,
  pending,
}: {
  profile: ProfileDto | null
  ranking: RankingDto | null
  pending: { code: string; name_es: string } | null
}) {
  const { user } = useAuth()
  const club = user?.player_club ?? null
  const displayName =
    profile?.display_name || user?.profile?.display_name || user?.username || ''
  const pendingFields = profile?.pending_profile_fields ?? []
  const me = ranking?.me ?? null

  return (
    <header
      id="perfil"
      className="flex flex-wrap items-center justify-between gap-md rounded-md border border-primary/40 bg-surface-elevated p-lg shadow-[0_0_34px_rgba(226,90,27,0.10)]"
    >
      <div className="flex min-w-0 items-center gap-md">
        <ClubMark
          logoUrl={club?.logo_url}
          name={club?.name}
          size={52}
          radius={10}
          fallbackColor={club?.primary_color}
        />
        <div className="min-w-0">
          <h1 className="m-0 break-words">Hola, {displayName}</h1>
          <div className="mt-xs flex flex-wrap items-center gap-sm text-xs text-text-muted">
            <span>{club ? club.name : 'Sin club asignado'}</span>
            {me && ranking && (
              <a href="#ranking" className="tag-ok no-underline">
                #{me.rank} de {ranking.total}
              </a>
            )}
            {pendingFields.length > 0 && (
              <Link to="/data/profile" className="btn px-sm py-xs text-xs no-underline">
                Completar perfil ({pendingFields.length}{' '}
                {pendingFields.length === 1 ? 'falta' : 'faltan'})
              </Link>
            )}
          </div>
        </div>
      </div>

      <NextStep pending={pending} />
    </header>
  )
}

/**
 * El próximo paso, uno solo: si hay un cuestionario asignado sin responder es
 * ése (no bloquea entrar al VR — el gate vive adentro del vr-app, doc 32 §4 —
 * pero se lo va a pedir igual); si no, jugar.
 */
function NextStep({ pending }: { pending: { code: string; name_es: string } | null }) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (pending) {
    return (
      <div className="flex flex-col items-start gap-xs sm:items-end">
        <Link
          to={`/data/${encodeURIComponent(pending.code)}`}
          className="btn-primary no-underline"
        >
          Responder {pending.code} →
        </Link>
        <span className="text-xs text-text-muted">Cuestionario asignado pendiente</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start gap-xs sm:items-end">
      <button
        type="button"
        className="btn-primary"
        disabled={busy}
        onClick={() => {
          setBusy(true)
          setError(null)
          openVrApp().catch((e: unknown) => {
            setError(vrBridgeError(e))
            setBusy(false)
          })
        }}
      >
        {busy ? 'Abriendo…' : 'Jugar →'}
      </button>
      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// 2. Performance (pocas cifras; el resto, plegado)
// ---------------------------------------------------------------------------

function PerformanceSection({
  perf,
  signals,
}: {
  perf: PerformanceDto | null
  signals: ReturnType<typeof buildSignalsModel> | null
}) {
  const summary = perf?.summary
  const hasTrials = (summary?.total_trials ?? 0) > 0

  return (
    <section id="performance">
      <h2 className="m-0 mb-md">Performance</h2>
      {perf === null ? (
        <div className="card border-primary/30">
          <p className="m-0 text-sm text-text-muted">Cargando…</p>
        </div>
      ) : !hasTrials ? (
        <div className="card border-primary/30">
          <p className="m-0 text-sm text-text-muted">
            Todavía no hay tiros registrados. Cerrá una sesión en el VR y acá
            aparecen goles, volumen y score.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-md">
          <div className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-5">
            <Metric label="Sesiones" value={summary!.total_sessions} />
            <Metric label="Tiros" value={summary!.total_trials} />
            <Metric
              label="Efectividad"
              value={
                summary!.goal_rate === null
                  ? '—'
                  : `${Math.round(summary!.goal_rate * 100)}%`
              }
              hint={`${summary!.goals} goles`}
            />
            <Metric
              label="Score promedio"
              value={
                summary!.average_score === null ? '—' : summary!.average_score.toFixed(2)
              }
              hint={
                summary!.best_score === null
                  ? undefined
                  : `mejor ${summary!.best_score.toFixed(2)}`
              }
            />
            <Metric
              label="Índice de autocontrol"
              value={
                signals?.summary.lastMeanRCardio != null
                  ? String(Math.round(signals.summary.lastMeanRCardio * 100))
                  : '—'
              }
              hint="última sesión, sobre 100"
            />
          </div>

          <Collapsible title="Detalle de resultados y señales">
            <div className="flex flex-col gap-lg">
              <div>
                <div className="mb-sm text-xs uppercase tracking-wide text-text-muted">
                  Distribución de resultados
                </div>
                <div className="flex flex-col gap-sm">
                  {Object.entries(perf.outcomes).map(([code, value]) => (
                    <OutcomeBar
                      key={code}
                      label={OUTCOME_LABELS[code] ?? code}
                      value={value}
                      total={summary!.total_trials}
                    />
                  ))}
                </div>
              </div>
              <SignalsSummary model={signals} />
            </div>
          </Collapsible>
        </div>
      )}
    </section>
  )
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string
  value: string | number
  hint?: string
}) {
  return (
    <div className="card border-primary/30">
      <div className="text-xs uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-xs font-display text-xl font-bold text-primary">{value}</div>
      {hint && <div className="mt-xs text-xs text-text-muted">{hint}</div>}
    </div>
  )
}

function OutcomeBar({
  label,
  value,
  total,
}: {
  label: string
  value: number
  total: number
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div>
      <div className="mb-xs flex justify-between text-xs text-text-muted">
        <span>{label}</span>
        <span>
          {value} · {pct}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-surface-highlight">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

const OUTCOME_LABELS: Record<string, string> = {
  goal: 'Gol',
  saved: 'Atajada',
  miss: 'Errado',
  post: 'Palo',
  out: 'Afuera',
  timeout: 'Tiempo',
  aborted: 'Abortado',
  unknown: 'Sin clasificar',
}

// ---------------------------------------------------------------------------
// 3. Cuestionarios y perfil — al final, plegados
// ---------------------------------------------------------------------------

/**
 * Sólo los cuestionarios ASIGNADOS (SMTQ, CSAI-2R). Los chequeos previos
 * (`PRE-SESION`, uno por sesión) ya no se listan acá: desde el 2026-09-07
 * cada uno vive en la fila de SU sesión, que es donde significa algo.
 */
function QuestionnairesBlock({ responses }: { responses: ResponseSummary[] | null }) {
  const assigned = responses?.filter((r) => r.code !== PRE_SESSION_CODE) ?? null

  return (
    <section id="cuestionarios">
      <Collapsible
        title="Cuestionarios"
        hint={assigned === null ? 'cargando…' : `${assigned.length} respondidos`}
      >
        {assigned === null ? (
          <p className="m-0 text-sm text-text-muted">Cargando…</p>
        ) : assigned.length === 0 ? (
          <p className="m-0 text-sm text-text-muted">
            Todavía no respondiste ninguno. Cuando el equipo te asigne uno te
            llega una invitación por mail.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-xs p-0">
            {assigned.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-sm text-sm">
                <span className="min-w-0">
                  <span className="font-bold">{r.code}</span>{' '}
                  <span className="text-xs text-text-muted">{r.name_es ?? ''}</span>
                </span>
                <span className="tag-ok shrink-0 normal-case tracking-normal">
                  {new Date(r.submitted_at).toLocaleDateString('es-AR')}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="m-0 mt-md text-xs text-text-muted">
          Los puntajes te los da el equipo personalmente. El chequeo previo de
          cada sesión se responde en el casco y aparece en su sesión.
        </p>
      </Collapsible>
    </section>
  )
}

function ProfileBlock({ profile }: { profile: ProfileDto | null }) {
  const pendingFields = profile?.pending_profile_fields ?? []
  const complete = profile !== null && pendingFields.length === 0

  return (
    <section id="perfil-detalle">
      {/* key: el perfil llega async y Collapsible sólo lee defaultOpen al
          montar. Incompleto → abierto, así el botón se ve sin buscarlo. */}
      <Collapsible
        key={profile === null ? 'loading' : complete ? 'complete' : 'pending'}
        defaultOpen={profile !== null && !complete}
        title="Perfil"
        hint={
          profile === null
            ? 'cargando…'
            : complete
              ? 'completo'
              : `faltan ${pendingFields.length}`
        }
      >
        <dl className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-5">
          <Fact label="Edad" value={profile?.age ? `${profile.age} años` : '—'} />
          <Fact
            label="Posición"
            value={labelOf(profile?.position ?? null, POSITION_LABELS)}
          />
          <Fact label="Pie dominante" value={footLabel(profile?.dominant_foot ?? null)} />
          <Fact
            label="Altura"
            value={profile?.height_cm ? `${profile.height_cm} cm` : '—'}
          />
          <Fact
            label="Experiencia VR"
            value={labelOf(profile?.vr_experience ?? null, VR_EXPERIENCE_LABELS)}
          />
        </dl>
        <Link
          to="/data/profile"
          className="btn mt-md inline-block text-xs no-underline"
        >
          {complete ? 'Editar perfil →' : 'Completar perfil →'}
        </Link>
      </Collapsible>
    </section>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="m-0 mt-xs truncate text-sm font-medium text-text-primary">
        {value}
      </dd>
    </div>
  )
}
